import React from 'react';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader } from './ui/card';
import { ImageGallery } from './ImageGallery';
import { useLang } from '../i18n/LanguageContext';

export const ProjectCard = ({
  projectName,
  brand,
  year,
  role,
  description,
  objectives,
  results,
  skills,
  mediaType = 'placeholder',
  mediaAspect = '16:9',
  videoUrl,
  imageUrl,
  images,
  media,
  behanceUrl,
}) => {
  const { t } = useLang();
  const renderMedia = () => {
    // Gallery with multiple images/videos (NEW: Mixed media support)
    if (mediaType === 'gallery' && ((media && media.length > 0) || (images && images.length > 0))) {
      // Mobile uses a portrait-friendly aspect (3:4) so vertical photos breathe.
      // Desktop keeps the editorial widescreen aspect.
      const desktopAspect =
        mediaAspect === '16:9' ? 'sm:aspect-[16/9]'
        : mediaAspect === '4:3' ? 'sm:aspect-[4/3]'
        : 'sm:aspect-square';
      return (
        <div
          className={`relative w-full overflow-hidden bg-muted/10 aspect-[3/4] ${desktopAspect}`}
        >
          <ImageGallery media={media} images={images} alt={projectName} />
        </div>
      );
    }

    // Single video
    if (mediaType === 'video' && videoUrl) {
      return (
        <div
          className="relative w-full overflow-hidden"
          style={{
            aspectRatio: mediaAspect === '16:9' ? '16/9' : mediaAspect === '4:3' ? '4/3' : '1/1',
          }}
        >
          <iframe
            src={videoUrl}
            title={projectName}
            className="w-full h-full"
            allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          />
        </div>
      );
    }

    // Single image
    if (mediaType === 'image' && imageUrl) {
      return (
        <div
          className="relative w-full overflow-hidden"
          style={{
            aspectRatio: mediaAspect === '16:9' ? '16/9' : mediaAspect === '4:3' ? '4/3' : '1/1',
          }}
        >
          <img
            src={imageUrl}
            alt={projectName}
            className="w-full h-full object-cover"
          />
        </div>
      );
    }

    // Placeholder (default)
    return (
      <div
        className="media-placeholder"
        style={{
          aspectRatio: mediaAspect === '16:9' ? '16/9' : mediaAspect === '4:3' ? '4/3' : '1/1',
        }}
      >
        <div className="text-center space-y-2">
          <svg
            className="w-12 h-12 mx-auto text-muted-foreground/50"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            {mediaType === 'video' ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            )}
          </svg>
          <p className="text-sm font-medium text-muted-foreground">
            {mediaType === 'video'
              ? '[ Video ]'
              : mediaType === 'gallery'
              ? `[ Galería — ${images?.length || 0} imágenes ]`
              : '[ Imagen del Proyecto ]'}
          </p>
        </div>
      </div>
    );
  };

  return (
    <Card className="project-card overflow-hidden h-full flex flex-col">
      {/* Media Area */}
      {renderMedia()}

      {/* Content Area */}
      <CardContent className="p-6 lg:p-8 flex-1 flex flex-col">
        {/* Header Info */}
        <div className="space-y-3 mb-6">
          {/* Project Name */}
          <h3 className="text-2xl lg:text-3xl font-semibold text-foreground">
            {projectName}
          </h3>

          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
            <span className="font-medium">{brand}</span>
            <span className="w-1 h-1 rounded-full bg-muted" />
            <span>{year}</span>
            <span className="w-1 h-1 rounded-full bg-muted" />
            <span className="italic">{role}</span>
          </div>
        </div>

        {/* Description */}
        <div className="space-y-4 mb-6">
          <p className="text-base text-muted-foreground leading-relaxed">
            {description}
          </p>
          
          {/* Behance Link - Optional */}
          {behanceUrl && (
            <a
              href={behanceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-medium text-accent hover:text-accent-hover transition-colors group"
            >
              {t.projects.viewFull}
              <svg
                className="w-4 h-4 transition-transform group-hover:translate-x-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </a>
          )}
        </div>

        {/* Objectives */}
        {objectives && objectives.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold uppercase tracking-wider text-foreground mb-3">
              {t.projects.objectivesLabel}
            </h4>
            <ul className="space-y-2">
              {objectives.map((objective, index) => (
                <li
                  key={index}
                  className="text-sm text-muted-foreground flex items-start gap-2"
                >
                  <span className="text-accent mt-1">•</span>
                  <span>{objective}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Results */}
        {results && (
          <div className="mb-6 bg-muted/10 border border-border rounded-sm p-4">
            <h4 className="text-sm font-semibold uppercase tracking-wider text-foreground mb-2">
              {t.projects.resultsLabel}
            </h4>
            <p className="text-sm text-muted-foreground">{results}</p>
          </div>
        )}

        {/* Skills/Services Tags */}
        <div className="mt-auto pt-6 border-t border-border">
          <div className="flex flex-wrap gap-2">
            {skills.map((skill, index) => (
              <span key={index} className="tag-chip">
                {skill}
              </span>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
